/**
 * AdminSmokeTest — Iter-233 post-deploy verification tool.
 *
 * One-click probe that calls `GET /api/admin/smoke-test` and renders
 * a green/yellow/red status with per-check breakdown. Useful immediately
 * after a `Deploy → app.ievntur.com` push to confirm production is healthy.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Activity, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw, ChevronLeft } from 'lucide-react';

const StatusPill = ({ status }) => {
  const map = {
    ok:   ['bg-emerald-100 text-emerald-700', CheckCircle2, 'OK'],
    warn: ['bg-amber-100 text-amber-700',   AlertTriangle, 'WARN'],
    fail: ['bg-rose-100 text-rose-700',     XCircle,       'FAIL'],
  };
  const [cls, Icon, label] = map[status] || ['bg-slate-100 text-slate-600', AlertTriangle, status?.toUpperCase()];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded-full ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const AdminSmokeTest = () => {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setRunning(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.get('/admin/smoke-test');
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Request failed');
    } finally {
      setRunning(false);
    }
  };

  const overallTone = result?.status === 'green'
    ? 'bg-emerald-500'
    : result?.status === 'yellow'
    ? 'bg-amber-500'
    : result?.status === 'red'
    ? 'bg-rose-500'
    : 'bg-slate-300';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16" data-testid="admin-smoke-test">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
        <button
          onClick={() => navigate('/admin/home')}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="back-to-home"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Admin Home
        </button>

        <header className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-wider">
                <Activity className="w-4 h-4" /> Post-Deploy Smoke Test
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
                Verify production health in one click
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Runs read-only checks: database · env · user accounts · packages catalog · Stripe · post-event recap.
                Safe to run after every deploy. No writes, no charges.
              </p>
            </div>
            <button
              onClick={run}
              disabled={running}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:opacity-60"
              data-testid="run-smoke-test-btn"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {running ? 'Running…' : 'Run Smoke Test'}
            </button>
          </div>
        </header>

        {err && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700" data-testid="smoke-test-error">
            Error: {err}
          </div>
        )}

        {result && (
          <>
            <div className={`rounded-2xl p-5 text-white ${overallTone}`} data-testid="smoke-test-banner">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold opacity-80">Overall Status</div>
                  <div className="text-2xl font-extrabold">
                    {result.status === 'green' ? 'All systems green' : result.status === 'yellow' ? 'Warnings present' : 'Failures detected'}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="px-3 py-1.5 bg-white/15 rounded-lg">
                    <div className="text-xs opacity-80">OK</div>
                    <div className="text-xl font-extrabold" data-testid="smoke-ok-count">{result.ok_count}</div>
                  </div>
                  <div className="px-3 py-1.5 bg-white/15 rounded-lg">
                    <div className="text-xs opacity-80">Warn</div>
                    <div className="text-xl font-extrabold" data-testid="smoke-warn-count">{result.warn_count}</div>
                  </div>
                  <div className="px-3 py-1.5 bg-white/15 rounded-lg">
                    <div className="text-xs opacity-80">Fail</div>
                    <div className="text-xl font-extrabold" data-testid="smoke-fail-count">{result.fail_count}</div>
                  </div>
                </div>
              </div>
              <div className="text-xs opacity-80 mt-3">
                Env: <span className="font-bold uppercase">{result.environment}</span> · Ran: {new Date(result.ran_at).toLocaleString()}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Check</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Detail</th>
                    <th className="text-right px-4 py-2 font-semibold">Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {result.checks.map((c) => (
                    <tr key={c.name} className="border-b border-slate-100" data-testid={`smoke-check-${c.name}`}>
                      <td className="px-4 py-2.5 font-bold text-slate-900">{c.name.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2.5"><StatusPill status={c.status} /></td>
                      <td className="px-4 py-2.5 text-slate-600">{c.detail}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500">{c.latency_ms} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSmokeTest;
